import React, { useEffect, useState, Children } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { ArrowRight, Lock, Mail, User as UserIcon, Eye, EyeOff, AlertCircle, ShoppingBag, Plus } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useCart } from '../../../contexts/CartContext';
import { shopData } from './shopData';
import { bookData } from './bookData';
import { createCheckoutSession, STRIPE_PUBLISHABLE_KEY } from '../../../utils/stripe';
import { saveCheckoutShippingAddressSnapshot } from '../../../utils/firebase';
import type { CartItem } from '../../../contexts/CartContext';
import type { CheckoutAddressSnapshot, CheckoutLineItemInput, CheckoutShippingAddress } from '../../../utils/stripe';
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
const CHECKOUT_ADDRESS_SNAPSHOT_KEY = 'sn_checkout_shipping_address';
function saveCheckoutAddressSnapshot(sessionId: string, address: CheckoutShippingAddress | undefined) {
  if (!address) return;
  const snapshot: CheckoutAddressSnapshot = {
    sessionId,
    address
  };
  window.sessionStorage.setItem(CHECKOUT_ADDRESS_SNAPSHOT_KEY, JSON.stringify(snapshot));
}

// Resolve the product image for a cart item (supplement image or book cover).
function getCartItemImage(item: CartItem): string | undefined {
  const name = item.name?.trim().toLowerCase();
  const normalizedName = name?.replace(/[^a-z]/g, '');
  if (item.type === 'book') {
    const b = bookData.find(x => x.id === item.productId) ?? bookData.find(x => x.title?.trim().toLowerCase() === name);
    return b?.coverImage;
  }
  const p = shopData.find(s => s.id === item.productId) ?? shopData.find(s => s.name?.trim().toLowerCase() === name) ?? shopData.find(s => {
    const catalogName = s.name.toLowerCase().replace(/[^a-z]/g, '');
    return Boolean(normalizedName && (catalogName.startsWith(normalizedName) || normalizedName.startsWith(catalogName)));
  });
  return p?.image;
}
// Build a Stripe-compatible line item reference from a cart item.
// Priority: 1) lookupKey on the item, 2) stripePriceId on the item,
//           3) shopData lookup by lookupKey (supplements), 4) null.
function getCheckoutRefFor(item: CartItem): Pick<CheckoutLineItemInput, 'priceId' | 'lookupKey'> | null {
  if (item.lookupKey) return {
    lookupKey: item.lookupKey
  };
  if (item.stripePriceId) return {
    priceId: item.stripePriceId
  };
  const supplement = shopData.find(p => p.id === item.productId);
  if (supplement?.lookupKey) return {
    lookupKey: supplement.lookupKey
  };
  return null;
}
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.2, 0.65, 0.3, 0.9]
    }
  }
};
function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}
function mapAuthError(err: any): string {
  const code = err?.code ?? '';
  if (code === 'auth/email-already-in-use') return 'An account with this email already exists. Sign in instead.';
  if (code === 'auth/invalid-credential' || code === 'auth/wrong-password' || code === 'auth/user-not-found') return 'Invalid email or password.';
  if (code === 'auth/weak-password') return 'Password must be at least 6 characters.';
  if (code === 'auth/invalid-email') return 'Please enter a valid email.';
  return err?.message ?? 'Something went wrong. Please try again.';
}
type Mode = 'signin' | 'signup';
export function Checkout() {
  const navigate = useNavigate();
  const {
    user,
    loading: authLoading,
    signIn,
    signUp
  } = useAuth();
  const {
    items,
    subtotal
  } = useCart();
  const [mode, setMode] = useState<Mode>('signup');
  // A return to checkout means payment was cancelled, so retain the cart and
  // discard the checkout-complete markers created before the Stripe redirect.
  useEffect(() => {
    window.sessionStorage.removeItem('sn_checkout_clear_pending');
    window.sessionStorage.removeItem('sn_inventory_checkout_session');
    window.sessionStorage.removeItem(CHECKOUT_ADDRESS_SNAPSHOT_KEY);
  }, []);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasPhysicalItems = items.some(item => item.type === 'supplement');
  const shippingCost = hasPhysicalItems ? 500 : 0;
  const taxAmount = Math.round(subtotal * 0.08);
  const total = subtotal + shippingCost + taxAmount;

  // Empty cart guard
  if (!authLoading && items.length === 0) {
    return <div className="w-full min-h-screen flex flex-col items-center justify-center pt-32 pb-20 px-6 bg-[#FFFFFF]" data-id="element-2307" data-mp="171jbo9e245yt">
        <div className="max-w-2xl mx-auto text-center" data-id="element-2308" data-mp="1h3vd3qw0h5o3">
          <div className="w-20 h-20 bg-white border border-[#E5E5E5] shadow-sm rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-2309" data-mp="1c8g1l5lh7luu">
            <ShoppingBag className="w-8 h-8 text-[#9A9A90]" data-id="element-2310" data-mp="1q0e8ravmn7od" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#111111] tracking-tight mb-4" data-id="element-2311" data-mp="1l6jqmbvo70re">
            Your cart is empty.
          </h1>
          <p className="text-sm font-medium text-[#5A5A5A] mb-10" data-id="element-2312" data-mp="1uxwfcum69at7">
            Add a book or supplement to begin checkout.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-2313" data-mp="28w4b4z7h262s">
            <Link to="/books" className="px-8 py-4 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors inline-flex items-center justify-center gap-2 rounded-md shadow-sm" data-id="element-2314" data-mp="24vkctsb5wwf2">
              Browse Books <ArrowRight className="w-4 h-4" data-id="element-2315" data-mp="aiuhr7925h63" />
            </Link>
            <Link to="/shop" className="px-8 py-4 bg-white border border-[#E5E5E5] text-[#111111] hover:border-tealslate hover:text-tealslate text-sm font-bold transition-colors inline-flex items-center justify-center gap-2 rounded-md shadow-sm" data-id="element-2316" data-mp="29mswc4ssrkdv">
              Shop Supplements <ArrowRight className="w-4 h-4" data-id="element-2317" data-mp="2jjfjx1cicpjl" />
            </Link>
          </div>
        </div>
      </div>;
  }
  // Auth gate passed: show the payment step
  if (user && !authLoading) {
    return <PaymentStep data-id="element-2318" data-mp="2skvpmvsdo3hh" />;
  }
  // While auth is resolving, show a quiet loading state
  if (authLoading) {
    return <div className="w-full min-h-screen bg-[#F9F9F9] flex items-center justify-center" data-id="element-2319" data-mp="2xiou9ke18pfn">
        <span className="w-8 h-8 border-2 border-tealslate/30 border-t-tealslate rounded-full animate-spin" data-id="element-2320" data-mp="32c06k11m5yey" />
      </div>;
  }
  // Auth gate form
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (mode === 'signup') {
        if (!name.trim()) {
          setError('Please enter your name.');
          setSubmitting(false);
          return;
        }
        await signUp(email, password, name.trim(), marketingOptIn);
      } else {
        await signIn(email, password);
      }
      // Stay on /checkout — user is now signed in, the if(user) branch above renders
    } catch (err) {
      setError(mapAuthError(err));
    } finally {
      setSubmitting(false);
    }
  };
  return <div className="w-full min-h-screen bg-[#F9F9F9]" data-id="element-2321" data-mp="1oalnad0tf7fg">
      <section className="pt-32 pb-8 px-6" data-id="element-2322" data-mp="1jck31ornl22e">
        <div className="max-w-5xl mx-auto" data-id="element-2323" data-mp="1jdv7zrwu4fv3">
          <motion.div initial="hidden" animate="visible" variants={{
          visible: {
            transition: {
              staggerChildren: 0.1
            }
          }
        }} data-id="element-2324" data-mp="1jewm6595dih1">
            <motion.h1 variants={fadeUp} className="text-3xl md:text-4xl font-bold text-[#111111] tracking-tight mb-2" data-id="element-2325" data-mp="19kmrggwqg835">
              {mode === 'signup' ? 'Create an account to continue.' : 'Sign in to continue.'}
            </motion.h1>
            <motion.p variants={fadeUp} className="text-sm font-medium text-[#5A5A5A]" data-id="element-2326" data-mp="1anbhw52q9r9i">
              An account lets you track orders, access digital downloads, and
              save info for next time.
            </motion.p>
          </motion.div>
        </div>
      </section>

      <section className="pb-24 px-6" data-id="element-2327" data-mp="15oqjmb71dhnp">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-8 md:gap-12" data-id="element-2328" data-mp="15ps4f7dutiim">
          {/* Form */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="lg:col-span-3" data-id="element-2329" data-mp="1teuiy6d6yv8w">
            {/* Mode toggle */}
            <div className="flex bg-white border border-[#E5E5E5] rounded-md p-1 mb-8 shadow-sm" data-id="element-2330" data-mp="1kdd9v7y7bttp">
              <button type="button" onClick={() => {
              setMode('signup');
              setError(null);
            }} className={`flex-1 py-3 text-sm font-bold rounded transition-colors ${mode === 'signup' ? 'bg-tealslate text-white shadow-sm' : 'text-[#5A5A5A] hover:text-[#111111]'}`} data-id="element-2331" data-mp="1pawmy4j5y0k2">
                New Customer
              </button>
              <button type="button" onClick={() => {
              setMode('signin');
              setError(null);
            }} className={`flex-1 py-3 text-sm font-bold rounded transition-colors ${mode === 'signin' ? 'bg-tealslate text-white shadow-sm' : 'text-[#5A5A5A] hover:text-[#111111]'}`} data-id="element-2332" data-mp="1fqdsd6ohlq7">
                Returning Customer
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5" data-id="element-2333" data-mp="3dwdfhdn2hxwg">
              {mode === 'signup' && <div data-id="element-2334" data-mp="3duiwi4vcwfv0">
                  <label htmlFor="name" className="block text-sm font-bold text-[#111111] mb-2" data-id="element-2335" data-mp="3slrsqwtrkzh4">
                    Full Name
                  </label>
                  <div className="relative" data-id="element-2336" data-mp="3nt8h45en0eey">
                    <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9A9A90]" data-id="element-2337" data-mp="304ffbsbc3ccf" />
                    <input id="name" type="text" required value={name} onChange={e => setName(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm pl-11 pr-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" placeholder="Jane Smith" data-id="element-2338" data-mp="2v6noz2qc7yac" />
                  </div>
                </div>}

              <div data-id="element-2339" data-mp="2s6nh36guqtmy">
                <label htmlFor="email" className="block text-sm font-bold text-[#111111] mb-2" data-id="element-2340" data-mp="2s76oht8vlzmq">
                  Email
                </label>
                <div className="relative" data-id="element-2341" data-mp="2o64pndivfpla">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9A9A90]" data-id="element-2342" data-mp="2jdjwme7shgit" />
                  <input id="email" type="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm pl-11 pr-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" placeholder="you@example.com" data-id="element-2343" data-mp="2eeh23npfqsp3" />
                </div>
              </div>

              <div data-id="element-2344" data-mp="fxglufh4f2z1">
                <label htmlFor="password" className="block text-sm font-bold text-[#111111] mb-2" data-id="element-2345" data-mp="fxyfd4cef339">
                  Password
                </label>
                <div className="relative" data-id="element-2346" data-mp="k0v319gsppe2">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9A9A90]" data-id="element-2347" data-mp="oxd0rpdpkngw" />
                  <input id="password" type={showPassword ? 'text' : 'password'} required minLength={mode === 'signup' ? 6 : undefined} value={password} onChange={e => setPassword(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm pl-11 pr-12 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" placeholder={mode === 'signup' ? 'At least 6 characters' : '••••••••'} data-id="element-2348" data-mp="tx7v62wozv6p" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="password-toggle-button absolute right-4 top-1/2 -translate-y-1/2 text-[#9A9A90] hover:text-[#5A5A5A] transition-colors" tabIndex={-1} aria-label={showPassword ? 'Hide password' : 'Show password'} data-id="element-2349" data-mp="39o3teemtb9v0">
                    {showPassword ? <EyeOff className="w-4 h-4" data-id="element-2350" data-mp="32l5n3fjybuws" /> : <Eye className="w-4 h-4" data-id="element-2351" data-mp="32jun7qtmn7ay" />}
                  </button>
                </div>
                {mode === 'signin' && <div className="mt-3 text-right" data-id="element-2352" data-mp="3gbsoxgl0e0x3">
                    <Link to="/forgot-password" className="text-sm font-bold text-tealslate hover:text-navyslate transition-colors" data-id="element-2353" data-mp="3bdhaesjrr28j">
                      Forgot password?
                    </Link>
                  </div>}
              </div>

              {mode === 'signup' && <label className="flex items-start gap-3 cursor-pointer pt-2" data-id="element-2354" data-mp="2jo4i3uqedusu">
                  <input type="checkbox" checked={marketingOptIn} onChange={e => setMarketingOptIn(e.target.checked)} className="mt-1 w-4 h-4 accent-tealslate cursor-pointer" data-id="element-2355" data-mp="2xndxwtaoymwa" />
                  <span className="text-sm text-[#5A5A5A] leading-relaxed" data-id="element-2356" data-mp="cuitkzxg5x7c">
                    Send me updates on new books, supplements, and articles.
                  </span>
                </label>}

              {error && <div className="bg-burgundy/10 rounded-md p-4 flex gap-3 items-start" data-id="element-2357" data-mp="1ptcbvk02j89q">
                  <AlertCircle className="w-5 h-5 text-burgundy shrink-0 mt-0.5" data-id="element-2358" data-mp="1uu9rbotp1o7j" />
                  <p className="text-sm text-burgundy" data-id="element-2359" data-mp="1gtyxqe101vio">{error}</p>
                </div>}

              <button type="submit" disabled={submitting} className="w-full px-6 py-4 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors disabled:opacity-60 disabled:cursor-not-allowed rounded-md shadow-sm flex items-center justify-center gap-2 mt-4" data-id="element-2360" data-mp="1ls8ugt2dh46e">
                {submitting ? <>
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-2361" data-mp="1zt37jp0d7tbe" />
                    {mode === 'signup' ? 'Creating account…' : 'Signing in…'}
                  </> : <>
                    {mode === 'signup' ? 'Create Account & Continue' : 'Sign In & Continue'}
                    <ArrowRight className="w-4 h-4" data-id="element-2362" data-mp="2x8qrovdsnau3" />
                  </>}
              </button>
            </form>
          </motion.div>

          {/* Order Summary */}
          <motion.aside initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="lg:col-span-2" data-id="element-2363" data-mp="13n1e4clr8ysw">
            <CartSummary shippingCost={shippingCost} taxAmount={taxAmount} total={total} data-id="element-2364" data-mp="zn10uvae6tnf" />
          </motion.aside>
        </div>
      </section>
    </div>;
}
function PaymentStep() {
  const navigate = useNavigate();
  const {
    user,
    profile,
    updateProfile
  } = useAuth();
  const {
    items,
    subtotal
  } = useCart();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const savedAddresses = profile?.addresses ?? [];
  const defaultAddress = savedAddresses.find(a => a.isDefault) ?? savedAddresses[0];
  const [selectedAddressId, setSelectedAddressId] = useState<string>(defaultAddress ? defaultAddress.id : 'new');
  // Re-sync the selection once saved addresses load. When the user signs in
  // directly on /checkout, the profile (and its addresses) arrives AFTER this
  // component mounts, so the initial state falls back to 'new'. Whenever a
  // default address becomes available but the current selection is 'new' or no
  // longer matches a saved address, snap to the default so the saved address is
  // pre-selected instead of showing the blank manual form.
  useEffect(() => {
    if (!defaultAddress) return;
    const selectionIsValid = savedAddresses.some(a => a.id === selectedAddressId);
    if (!selectionIsValid) {
      setSelectedAddressId(defaultAddress.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedAddresses.length, defaultAddress?.id]);
  // Shipping Address Form State
  const [recipientName, setRecipientName] = useState(user?.displayName ?? '');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [stateProvince, setStateProvince] = useState('');
  const [zip, setZip] = useState('');
  const [country, setCountry] = useState('United States');
  const [newDeliveryInstructions, setNewDeliveryInstructions] = useState('');
  // Whether the address picker is expanded to show all saved addresses.
  const [expanded, setExpanded] = useState(false);
  const [savingAddr, setSavingAddr] = useState(false);
  const selectedAddr = savedAddresses.find(a => a.id === selectedAddressId);
  const hasPhysicalItems = items.some(item => item.type === 'supplement');
  const unbuyable = items.filter(i => !getCheckoutRefFor(i));
  const currentCountry = selectedAddressId === 'new' ? country : selectedAddr?.country || 'United States';
  const currentState = selectedAddressId === 'new' ? stateProvince : selectedAddr?.state || '';
  let shippingCost = 0;
  if (hasPhysicalItems) {
    const isUS = ['united states', 'us', 'usa'].includes(currentCountry.trim().toLowerCase());
    const stateLower = currentState.trim().toLowerCase();
    const isTier1 = ['alaska', 'ak', 'hawaii', 'hi'].includes(stateLower);
    const isTier2 = ['arizona', 'az', 'california', 'ca', 'idaho', 'id', 'nevada', 'nv', 'oregon', 'or', 'utah', 'ut', 'washington', 'wa'].includes(stateLower);
    if (!isUS) {
      shippingCost = 1500; // $15.00 International
    } else if (isTier1) {
      shippingCost = 1200; // $12.00 AK/HI
    } else if (isTier2) {
      shippingCost = 800; // $8.00 Western states
    } else {
      shippingCost = 600; // $6.00 Standard US
    }
  }
  const taxAmount = 0;
  const total = subtotal + shippingCost + taxAmount;
  // Live checkout — creates a Stripe Checkout Session and redirects the
  // customer to Stripe to complete payment. On success Stripe returns the
  // customer to /account?order=success.
  const handleCheckout = async () => {
    setError(null);
    setSubmitting(true);
    console.log('CHECKOUT CART ITEMS:', items);
    try {
      const lineItems = items.map(i => {
        const ref = getCheckoutRefFor(i);
        if (!ref) return null;
        return {
          ...ref,
          quantity: i.quantity
        };
      }).filter((x): x is NonNullable<typeof x> => x !== null);
      if (lineItems.length === 0) {
        setError('No items in your cart are available for purchase yet.');
        setSubmitting(false);
        return;
      }
      if (hasPhysicalItems && !selectedAddr) {
        setError('Please save and select a shipping address before checking out.');
        setSubmitting(false);
        return;
      }
      const shippingAddress: CheckoutShippingAddress | undefined = selectedAddr ? {
        recipientName: selectedAddr.recipientName,
        line1: selectedAddr.line1,
        ...(selectedAddr.line2 ? {
          line2: selectedAddr.line2
        } : {}),
        city: selectedAddr.city,
        state: selectedAddr.state,
        postalCode: selectedAddr.postalCode,
        country: selectedAddr.country,
        ...(selectedAddr.deliveryInstructions?.trim() ? {
          deliveryInstructions: selectedAddr.deliveryInstructions.trim()
        } : {})
      } : undefined;
      const {
        clientSecret,
        sessionId
      } = await createCheckoutSession({
        lineItems,
        shippingAddress
      });
      // Keep an immutable, session-keyed copy for fulfillment. This is
      // deliberately separate from the mutable address book and browser-only
      // return snapshot so admins can always resolve the address selected here.
      // A missing snapshot permission must never prevent a customer from paying.
      try {
        await saveCheckoutShippingAddressSnapshot(sessionId, shippingAddress);
      } catch (snapshotError) {
        console.error('[Checkout] Could not save fulfillment snapshot:', snapshotError);
      }
      // Stripe unloads this app for payment. Preserve the session identifier
      // so the return page can apply this paid order's inventory adjustment
      // once, then mark the cart as ready to clear.
      window.sessionStorage.setItem('sn_inventory_checkout_session', sessionId);
      saveCheckoutAddressSnapshot(sessionId, shippingAddress);
      setClientSecret(clientSecret);
      setSubmitting(false);
    } catch (err: any) {
      setError(err?.message ?? 'Something went wrong starting checkout.');
      setSubmitting(false);
    }
  };
  // Save a brand-new address to the profile right here on the checkout
  // screen, then select it and collapse the picker.
  const handleSaveNewAddress = async () => {
    if (!recipientName.trim() || !addressLine1.trim() || !city.trim() || !stateProvince.trim() || !zip.trim() || !country.trim()) {
      setError('Please fill out all required shipping address fields.');
      return;
    }
    setSavingAddr(true);
    setError(null);
    try {
      const newAddress = {
        id: Math.random().toString(36).substring(2, 9),
        recipientName: recipientName.trim(),
        line1: addressLine1.trim(),
        line2: addressLine2.trim(),
        city: city.trim(),
        state: stateProvince.trim(),
        postalCode: zip.trim(),
        country: country.trim(),
        deliveryInstructions: newDeliveryInstructions.trim(),
        isDefault: savedAddresses.length === 0
      };
      await updateProfile({
        addresses: [...savedAddresses, newAddress]
      });
      setSelectedAddressId(newAddress.id);
      setExpanded(false);
      setAddressLine1('');
      setAddressLine2('');
      setCity('');
      setStateProvince('');
      setZip('');
      setNewDeliveryInstructions('');
    } catch (err: any) {
      setError(err?.message ?? 'Could not save this address. Please try again.');
    } finally {
      setSavingAddr(false);
    }
  };
  if (clientSecret) {
    return <div className="min-h-screen flex flex-col lg:flex-row font-sans" data-id="element-2365" data-mp="28ayvzfec781k">
        {/* Left Column: Stripe Checkout */}
        <div className="w-full lg:w-1/2 bg-white pt-32 pb-24 px-6 lg:px-12 xl:px-8 flex flex-col items-center lg:items-end" data-id="element-2366" data-mp="2mj3j0qcjdiq3">
          <div className="w-full max-w-md" data-id="element-2367" data-mp="2ralljiu7jrd6">
            <EmbeddedCheckoutProvider stripe={stripePromise} options={{
            clientSecret
          }} data-id="element-2368" data-mp="2rboc1pnmbrsk">
              <EmbeddedCheckout data-id="element-2369" data-mp="2ih9m8t5goyv7" />
            </EmbeddedCheckoutProvider>
          </div>
        </div>

        {/* Right Column: Order Summary */}
        <div className="w-full lg:w-1/2 pt-32 pb-24 px-6 lg:px-12 xl:px-8 flex flex-col items-center lg:items-start bg-[#FBFBFB] lg:sticky lg:top-0 lg:h-screen lg:overflow-y-auto" data-id="element-2370" data-mp="367vvgnr7cp6s">
          <div className="w-full max-w-md" data-id="element-2371" data-mp="2w96ecx74r8rl">
            <CartSummary shippingCost={shippingCost} taxAmount={taxAmount} total={total} data-id="element-2372" data-mp="2w8erqbvigldg" />
          </div>
        </div>
      </div>;
  }
  return <div className="w-full min-h-screen bg-[#F9F9F9]" data-id="element-2373" data-mp="1ktrdhibsvwr7">
      <section className="pt-32 pb-24 px-6 bg-[#FFFFFF]" data-id="element-2374" data-mp="1ks6jpuxb8hue">
        <div className="max-w-5xl mx-auto flex flex-col gap-6" data-id="element-2375" data-mp="1psum1cqzol3x">
          <div className="w-full" data-id="element-2376" data-mp="1pq854mi7kt91">
            <h2 className="text-2xl font-bold text-[#111111] mb-4 tracking-tight" data-id="element-2377" data-mp="1i8a6xe5h94l4">
              Shipping Address
            </h2>

            <div className="bg-white border border-[#D1D1D1] shadow-sm rounded-md overflow-hidden" data-id="element-2378" data-mp="1d9grhg6mr3ib">
              {/* COLLAPSED — only the selected address with a subtle Change action */}
              {selectedAddressId !== 'new' && !expanded && selectedAddr && <div className="p-5" data-id="element-2379" data-mp="yksynho657w2">
                  <div className="flex items-start gap-4" data-id="element-2380" data-mp="ylvodm9r94ae">
                    <div className="pt-1" data-id="element-2381" data-mp="ujqwot2vahqe">
                      <span className="block w-4 h-4 rounded-full border-2 border-tealslate flex items-center justify-center" data-id="element-2382" data-mp="ul255679cdnu">
                        <span className="block w-2 h-2 rounded-full bg-tealslate" data-id="element-2383" data-mp="18cqfp51uyu2p" />
                      </span>
                    </div>
                    <div className="flex-1 flex items-start justify-between gap-4" data-id="element-2384" data-mp="ftt6y8pahjz3">
                      <div data-id="element-2385" data-mp="avrljh4e58kg">
                        <p className="font-bold text-[#111111] mb-1 flex items-center gap-2" data-id="element-2386" data-mp="augnlxxmmv19">
                          {selectedAddr.recipientName}
                          {selectedAddr.isDefault && <span className="bg-[#F9F9F9] text-[#5A5A5A] text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm font-bold" data-id="element-2387" data-mp="tgs9bpgp4ayi">
                              Default
                            </span>}
                        </p>
                        <p className="text-sm text-[#5A5A5A] leading-relaxed" data-id="element-2388" data-mp="138ftd5axappy">
                          {selectedAddr.line1}
                          {selectedAddr.line2 && <br data-id="element-2389" data-mp="1c79xrhuhwke2" />}
                          {selectedAddr.line2}
                          <br data-id="element-2390" data-mp="1h6walq6evymc" />
                          {selectedAddr.city}, {selectedAddr.state}{' '}
                          {selectedAddr.postalCode}
                          <br data-id="element-2391" data-mp="1m5h0avo1ezwj" />
                          {selectedAddr.country}
                        </p>
                      </div>
                      <button type="button" onClick={() => setExpanded(true)} className="checkout-change-button shrink-0 text-sm font-bold text-[#5A5A5A] hover:text-[#111111] transition-colors" data-id="element-2392" data-mp="3j0luqkmr62rr">
                        Change
                      </button>
                    </div>
                  </div>

                  {/* Delivery instructions — read-only display of the note
                   saved to this address (editable from the Account page). */}
                  {selectedAddr.deliveryInstructions?.trim() && <div className="mt-4 pl-8" data-id="element-2393" data-mp="2o13i8b0ssos9">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-[#9A9A90] mb-1.5" data-id="element-2394" data-mp="2nzshyl6k9z5w">
                        Delivery instructions
                      </p>
                      <p className="text-sm text-[#5A5A5A] leading-relaxed whitespace-pre-line" data-id="element-2395" data-mp="2ezm591iot37s">
                        {selectedAddr.deliveryInstructions}
                      </p>
                    </div>}
                </div>}

              {/* EXPANDED — all saved addresses stacked, plus add-new affordance */}
              {selectedAddressId !== 'new' && expanded && <AnimatePresence initial={false} data-id="element-2396" data-mp="yoi8btajqsfu">
                  <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} data-id="element-2397" data-mp="104dxv7hikp3h">
                    {savedAddresses.length > 0 && <div className="divide-y divide-[#E5E5E5]" data-id="element-2398" data-mp="r2czu8eghz0r">
                        {savedAddresses.map(addr => <label key={addr.id} className={`block p-5 cursor-pointer transition-colors ${selectedAddressId === addr.id ? 'bg-[#F9F9F9]' : 'bg-white hover:bg-[#FAFAFA]'}`} data-id="element-2399" data-mp="w0658z7xqozz">
                            <div className="flex items-start gap-4" data-id="element-2400" data-mp="m4bgdmd4bxor">
                              <div className="pt-1" data-id="element-2401" data-mp="8coi5gzm8f3c">
                                <input type="radio" name="shippingAddress" value={addr.id} checked={selectedAddressId === addr.id} onChange={() => {
                          setSelectedAddressId(addr.id);
                          setExpanded(false);
                        }} className="w-4 h-4 accent-tealslate" data-id="element-2402" data-mp="8a21mruqyp8z" />
                              </div>
                              <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2403" data-mp="3fvuvfook0arj">
                                <div data-id="element-2404" data-mp="3aycuakgs2zu3">
                                  <p className="font-bold text-[#111111] mb-1 flex items-center gap-2" data-id="element-2405" data-mp="3aze64r5mbqcv">
                                    {addr.recipientName}
                                    {addr.isDefault && <span className="bg-[#F9F9F9] text-[#5A5A5A] text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm font-bold" data-id="element-2406" data-mp="3kuy0va8g4j01">
                                        Default
                                      </span>}
                                  </p>
                                  <p className="text-sm text-[#5A5A5A] leading-relaxed" data-id="element-2407" data-mp="cg7a2zzpb3xb">
                                    {addr.line1}
                                    {addr.line2 && <br data-id="element-2408" data-mp="7j738mv11x55" />}
                                    {addr.line2}
                                    <br data-id="element-2409" data-mp="lej9nrijpc1w" />
                                    {addr.city}, {addr.state} {addr.postalCode}
                                    <br data-id="element-2410" data-mp="1ntymlex2bwr0" />
                                    {addr.country}
                                  </p>
                                </div>
                                {addr.deliveryInstructions?.trim() && <div className="sm:border-l sm:border-[#E5E5E5] sm:pl-4" data-id="element-2411" data-mp="1xsfkywybzdn3">
                                    <p className="text-[10px] font-bold uppercase tracking-wider text-[#9A9A90] mb-1" data-id="element-2412" data-mp="21trfngw2ap25">
                                      Delivery instructions
                                    </p>
                                    <p className="text-sm text-[#5A5A5A] leading-relaxed whitespace-pre-line" data-id="element-2413" data-mp="1a21xzkuls4zd">
                                      {addr.deliveryInstructions}
                                    </p>
                                  </div>}
                              </div>
                            </div>
                          </label>)}
                      </div>}
                    <div className="p-5 border-t border-[#E5E5E5]" data-id="element-2414" data-mp="qech48o7fp4x">
                      <button type="button" onClick={() => {
                    setRecipientName(user?.displayName ?? '');
                    setSelectedAddressId('new');
                  }} className="checkout-change-button inline-flex items-center gap-1.5 text-sm font-bold text-[#5A5A5A] hover:text-[#111111] transition-colors" data-id="element-2415" data-mp="qchtunpgnkxo">
                        <Plus className="w-4 h-4" data-id="element-2416" data-mp="4vk8kc4v7pfp" />
                        Add new address
                      </button>
                    </div>
                  </motion.div>
                </AnimatePresence>}

              {selectedAddressId === 'new' && <div className="p-6 md:p-8" data-id="element-2417" data-mp="3r9btb3jszgyj">
                  <div className="space-y-5" data-id="element-2418" data-mp="3mbs6hf91hvuz">
                    <div data-id="element-2419" data-mp="3mclesknnctf8">
                      <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-2420" data-mp="3m9ph8xfpo6rb">
                        Recipient Full Name *
                      </label>
                      <input type="text" required value={recipientName} onChange={e => setRecipientName(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-2421" data-mp="3da2d43vlkmuk" />
                    </div>
                    <div data-id="element-2422" data-mp="2dap575wpv6s9">
                      <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-2423" data-mp="2r85c0iixd2kj">
                        Address Line 1 *
                      </label>
                      <input type="text" required value={addressLine1} onChange={e => setAddressLine1(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-2424" data-mp="2ma3tpztbp7a4" />
                    </div>
                    <div data-id="element-2425" data-mp="23j2xvkni1rus">
                      <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-2426" data-mp="23ijhw9hetdje">
                        Address Line 2 (Optional)
                      </label>
                      <input type="text" value={addressLine2} onChange={e => setAddressLine2(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-2427" data-mp="1au5f7gfdlt1i" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-2428" data-mp="3dhlwu7j0tk0s">
                      <div data-id="element-2429" data-mp="3hi4q860kay53">
                        <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-2430" data-mp="3hjfue6xx67wq">
                          City *
                        </label>
                        <input type="text" required value={city} onChange={e => setCity(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-2431" data-mp="90rum3wb9hnu" />
                      </div>
                      <div className="grid grid-cols-2 gap-5" data-id="element-2432" data-mp="2krm1azaetjal">
                        <div data-id="element-2433" data-mp="2ku83skig18ly">
                          <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-2434" data-mp="2prrjzpypbvgj">
                            State / Province *
                          </label>
                          <input type="text" required value={stateProvince} onChange={e => setStateProvince(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-2435" data-mp="vcuipgc9lsgi" />
                        </div>
                        <div data-id="element-2436" data-mp="cljtr4q3npr9">
                          <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-2437" data-mp="cltkiuiyy0y0">
                            ZIP / Postal *
                          </label>
                          <input type="text" required value={zip} onChange={e => setZip(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-2438" data-mp="2ndt0qg14pnz" />
                        </div>
                      </div>
                    </div>
                    <div data-id="element-2439" data-mp="3awwvyumlns6m">
                      <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-2440" data-mp="3avbxwv12tr40">
                        Country *
                      </label>
                      <input type="text" required value={country} onChange={e => setCountry(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-2441" data-mp="3ft5962h8lbb5" />
                    </div>
                    <div data-id="element-2442" data-mp="2ww3z438tsl0c">
                      <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-2443" data-mp="2wxf426e0byt1">
                        Delivery Instructions (Optional)
                      </label>
                      <textarea rows={3} value={newDeliveryInstructions} onChange={e => setNewDeliveryInstructions(e.target.value)} placeholder="e.g. Leave at the front door, gate code is 1234…" className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] placeholder:text-[#9A9A90] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all resize-none" data-id="element-2444" data-mp="35xts3dicvs08" />
                    </div>

                    <div className="flex items-center gap-3 pt-2" data-id="element-2445" data-mp="esxh36418kg1">
                      <button type="button" onClick={handleSaveNewAddress} disabled={savingAddr} className="px-5 py-3 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors rounded-md shadow-sm disabled:opacity-60 inline-flex items-center justify-center gap-2" data-id="element-2446" data-mp="srdm8sm8y2w2">
                        {savingAddr ? <>
                            <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-2447" data-mp="xnkskmik1gej" />
                            Saving…
                          </> : 'Save address'}
                      </button>
                      {defaultAddress && <button type="button" onClick={() => {
                    setSelectedAddressId(defaultAddress.id);
                    setExpanded(false);
                  }} className="text-sm font-bold text-[#9A9A90] hover:text-[#111111] transition-colors" data-id="element-2448" data-mp="1lehxvgfuhfgt">
                          Cancel
                        </button>}
                    </div>
                    <p className="text-xs text-[#9A9A90] leading-relaxed" data-id="element-2449" data-mp="24yp18u99bhoc">
                      This address will be saved to your account for future
                      orders.
                    </p>
                  </div>
                </div>}
            </div>

            {error && <div className="mt-6 bg-burgundy/10 rounded-md p-4 flex gap-3 items-start" data-id="element-2450" data-mp="1ha5x8qx8pr22">
                <AlertCircle className="w-5 h-5 text-burgundy shrink-0 mt-0.5" data-id="element-2451" data-mp="1m3rxtkq4q4hi" />
                <p className="text-sm text-burgundy" data-id="element-2452" data-mp="17ece2op0mzbb">{error}</p>
              </div>}
          </div>

          <motion.aside initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="w-full" data-id="element-2453" data-mp="1cdfe1uq52fcf">
            <CartSummary shippingCost={shippingCost} taxAmount={taxAmount} total={total} data-id="element-2454" data-mp="13c7jnnqqtynm" />

            {unbuyable.length > 0 && <div className="mt-6 bg-burgundy/10 rounded-md p-4 flex gap-3 items-start" data-id="element-2455" data-mp="351wxjx6n9t2y">
                <AlertCircle className="w-5 h-5 text-burgundy shrink-0 mt-0.5" data-id="element-2456" data-mp="309wq7jo8vxwc" />
                <div className="text-sm text-burgundy" data-id="element-2457" data-mp="2vasi6wd2467l">
                  <p className="font-bold mb-1" data-id="element-2458" data-mp="2vbbr5nkqbk9h">
                    Some items aren't available for purchase yet:
                  </p>
                  <ul className="list-disc ml-5" data-id="element-2459" data-mp="3nzhbwabs999b">
                    {unbuyable.map(i => <li key={i.productId} data-id="element-2460" data-mp="3nxn0bmmeithw">{i.name}</li>)}
                  </ul>
                </div>
              </div>}
          </motion.aside>

<div className="w-full" data-id="element-2461" data-mp="3eysxvfme38wf">
      <button type="button" onClick={handleCheckout} disabled={submitting || items.length === 0} className="w-full px-6 py-4 bg-tealslate text-white font-bold hover:bg-navyslate transition-colors disabled:opacity-60 disabled:cursor-not-allowed rounded-md shadow-sm flex items-center justify-center gap-2 text-[18px]" data-id="element-2462" data-mp="3exzvequgr5k3">
        {submitting ? <>
            <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-2463" data-mp="1yn5q1an0n9zy" />
            Preparing secure payment…
          </> : <>
            Continue to Payment
          </>}
      </button>

      <p className="text-sm text-[#5A5A5A] text-center mt-4" data-id="element-2464" data-mp="n3oxmrs67g3w">
        Secure payment processed by Stripe
      </p>
</div>
        </div>
      </section>
    </div>;
}
function CartSummary({
  shippingCost,
  taxAmount,
  total
}: {
  shippingCost: number;
  taxAmount: number;
  total: number;
}) {
  const {
    items,
    subtotal,
    itemCount
  } = useCart();
  return <div className="w-full" data-id="element-2465" data-mp="11ufpfztrif70">
      <h2 className="text-2xl font-bold text-[#111111] mb-4 tracking-tight" data-id="element-2466" data-mp="16qwgb1scj7ns">
        Order Summary
      </h2>
      <div className="bg-transparent rounded-md" data-id="element-2467" data-mp="1atazjmzxg9fl">
        <div className="px-6 py-4 flex items-center justify-end bg-transparent" data-id="element-2468" data-mp="1aqx4vf95sz8k">
          <span className="text-sm font-medium text-[#9A9A90]" data-id="element-2469" data-mp="38kdlzzwx1yfw">Price</span>
        </div>
        <ul data-id="element-2470" data-mp="3cl5x6ypcmdet">
        {items.map(item => {
          const image = getCartItemImage(item);
          return <li key={item.productId} className="px-6 py-5 flex gap-4 bg-transparent" data-id="element-2471" data-mp="2tmul5msq6g7i">
              {/* Image */}
              {image && <div className="w-20 h-20 shrink-0 flex items-center justify-center" data-id="element-2472" data-mp="2omyhxpruqst0">
                  <img src={image} alt="" className="max-w-full max-h-full object-contain" data-id="element-2473" data-mp="32fxt5fklq0ur" />
                </div>}

              {/* Details */}
              <div className="flex-1 min-w-0" data-id="element-2474" data-mp="2ap04qnp9p2f3">
                <p className="text-sm sm:text-base font-bold text-[#111111] leading-snug" data-id="element-2475" data-mp="2aptezynfqa1z">
                  {item.name}
                </p>
                <p className="text-sm text-tealslate font-medium mt-1" data-id="element-2476" data-mp="2jpyd1i6qnw3h">
                  In Stock
                </p>
                <p className="text-xs font-bold uppercase tracking-wider text-[#9A9A90] mt-1" data-id="element-2477" data-mp="2eqdakxsd15m0">
                  {item.type === 'book' ? 'Digital Book' : 'Supplement'} · Qty{' '}
                  {item.quantity}
                </p>
              </div>

              {/* Price */}
              <div className="shrink-0 text-right" data-id="element-2478" data-mp="2hoa0yzn7l3w">
                <p className="text-sm sm:text-base font-bold text-[#111111] whitespace-nowrap" data-id="element-2479" data-mp="2g3eb61ldw4g">
                  {formatPrice(item.unitPrice * item.quantity)}
                </p>
              </div>
            </li>;
        })}
      </ul>
      <div className="px-6 py-5 text-right space-y-2 bg-transparent" data-id="element-2480" data-mp="3or7oiuyflmoa">
        <div className="flex justify-between text-sm text-[#5A5A5A]" data-id="element-2481" data-mp="3tohimnwrmmig">
          <span data-id="element-2482" data-mp="l7waf9byjxge">Subtotal ({itemCount} {itemCount === 1 ? 'item' : 'items'}):</span>
          <span data-id="element-2483" data-mp="q7qyz689gaya">{formatPrice(subtotal)}</span>
        </div>
        <div className="flex justify-between text-sm text-[#5A5A5A]" data-id="element-2484" data-mp="q9bscsiu9nuk">
          <span data-id="element-2485" data-mp="bhjr1kfvqabu">Shipping:</span>
          <span data-id="element-2486" data-mp="bg8jd9jbcifg">{shippingCost === 0 ? 'Free' : formatPrice(shippingCost)}</span>
        </div>
        <div className="flex justify-between text-sm text-[#5A5A5A]" data-id="element-2487" data-mp="gfcva7xmgu9h">
          <span data-id="element-2488" data-mp="1468jkv2vd7bz">Estimated Tax:</span>
          <span data-id="element-2489" data-mp="14576ym67gcs5">{formatPrice(taxAmount)}</span>
        </div>
        <div className="flex justify-between text-lg font-bold text-[#111111] pt-3 border-t border-[#E5E5E5] mt-3" data-id="element-2490" data-mp="18yihaxa87bot">
          <span data-id="element-2491" data-mp="27nebbmxjypba">Total:</span>
          <span data-id="element-2492" data-mp="2cnslbaips9by">{formatPrice(total)}</span>
        </div>
      </div>
    </div>
  </div>;
}